import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import { helpMessageBody, type HelpSupportAssignee, type HelpSupportRequest, type HelpSupportStatus } from '@/services/help-support'
import type { ApiResponse } from '@/types'

/** not_billed: a company a parent org created by hand (no trial, card or subscription). */
/** exempt: a card-exempt country (India) after its trial: carries on, never charged. */
export type MasterAdminPlan = 'trial' | 'active' | 'exempt' | 'locked' | 'not_billed'

export interface MasterAdminBilling {
  subscription_status: 'trial' | 'active' | 'expired' | null
  plan: MasterAdminPlan
  trial_start_on: string | null
  trial_end_on: string | null
  trial_days_remaining: number | null
  is_locked: boolean
  lock_reason: string | null
  user_count: number
  admin_count: number | null
  worker_count: number | null
  has_card?: boolean
  card_exempt?: boolean
}

export interface MasterAdminOrganization {
  id: number
  instance_id: string
  name: string
  identifier: string
  vertical: string
  country: string
  owner_name: string
  owner_email: string
  status: string
  /** ServiceRegistrationChoices: 0 Pending, 1 Approved, 2 Rejected, 3 Suspended, 4 Terminated. */
  status_code: number
  created_on: string | null
  branch_count: number
  /** False for companies a parent org created by hand. */
  billed?: boolean
  parent_org?: { id: number; name: string; identifier: string; is_root: boolean } | null
  billing: MasterAdminBilling
}

/** A product parent org under the Root Org (or, is_root, the companies directly under it). */
export interface MasterAdminParentOrg {
  id: number
  instance_id: string
  name: string
  identifier: string
  is_root: boolean
  status: string
  status_code: number
  created_on: string | null
  organizations: number
  terminated: number
  users: number
  plans: Partial<Record<MasterAdminPlan, number>>
}

export interface MasterAdminOverview {
  root_org: { instance_id: string; name: string }
  organizations: MasterAdminOrganization[]
  parent_orgs?: MasterAdminParentOrg[]
  totals: {
    organizations: number
    /** Terminated orgs: listed in `organizations` but left out of every other total. */
    terminated: number
    users: number
    branches: number
    plans: Partial<Record<MasterAdminPlan, number>>
    open_support_requests: number
  }
  signups_by_month: { month: string; label: string; count: number }[]
  settings: PlatformSettings
}

export interface PlatformSettings {
  trial_days: number
  signup_alert_emails: string[]
  support_alert_emails: string[]
  stripe_configured: boolean
  /** False until someone saves: the values are still the environment defaults. */
  customized: boolean
  updated_at: string | null
  updated_by: string
  limits: Record<'trial_days', { min: number; max: number }>
}

export type PlatformSettingsUpdate = Partial<Pick<PlatformSettings,
  'trial_days' | 'signup_alert_emails' | 'support_alert_emails'>> & { reason: string }

export interface MasterAdminInvoice {
  id: string
  number: string
  organization: string
  organization_instance_id: string | null
  status: string | null
  currency: string
  amount_due: number
  amount_paid: number
  created: number | null
  due_date: number | null
  hosted_invoice_url: string | null
}

export interface CurrencyTotal {
  currency: string
  amount: number
  organizations: number
}

export interface BillingCard {
  brand: string
  last4: string
  exp_month: number | null
  exp_year: number | null
  expiring_soon: boolean
}

export interface BillingOrganization {
  id: number
  instance_id: string
  name: string
  identifier: string
  country: string
  status: string
  plan: MasterAdminPlan
  is_locked: boolean
  card_exempt: boolean
  user_count: number
  admin_count: number | null
  worker_count: number | null
  trial_end_on: string | null
  trial_days_remaining: number | null
  currency: string
  interval: 'month' | 'year' | ''
  billed_admins: number | null
  billed_workers: number | null
  /** What Stripe bills per interval (a year's worth on annual plans). */
  total_amount: number | null
  /** total_amount a month (annual plans: a twelfth). */
  monthly_amount: number | null
  card: BillingCard | null
  last_synced_at: string | null
  stripe_customer_url: string
  stripe_subscription_url: string
}

export interface FailedPayment extends MasterAdminInvoice {
  organization_id: number | null
  attempt_count: number
  next_payment_attempt: number | null
  stripe_url: string
}

export interface MasterAdminBillingReport {
  configured: boolean
  stripe_mode: 'test' | 'live' | null
  mrr: CurrencyTotal[]
  trial_pipeline: CurrencyTotal[]
  counts: {
    paying: number
    trials: number
    trials_with_card: number
    trials_ending_soon: number
    trials_ending_without_card: number
    locked: number
    cards_expiring: number
    failed_payments: number
  }
  trial_ending_days: number
  /** Currencies customers are billed in that are missing a licence price or the minimum. */
  pricing_gaps: { currency: string; organizations: number; missing: string[] }[]
  organizations: BillingOrganization[]
  failed_payments: FailedPayment[]
  stripe_error: string | null
}

export interface IntegrationCostOperation {
  provider: string
  provider_label: string
  operation: string
  operation_label: string
  calls: number
  units: number
  cost: number
  platform_cost: number
}

export interface IntegrationCostOrganization {
  organization_id: number | null
  organization: string
  calls: number
  cost: number
  platform_cost: number
  twilio_cost: number
  google_maps_cost: number
  whatsapp_cost: number
  whatsapp_messages: number
}

export interface IntegrationCostReport {
  month: string
  currency: string
  total_cost: number
  platform_cost: number
  calls: number
  by_operation: IntegrationCostOperation[]
  by_organization: IntegrationCostOrganization[]
  by_day: { date: string; cost: number; calls: number }[]
}

export interface PricingTier {
  key: string
  label: string
  price: string
  price_suffix: string
  title: string
  description: string
  style: string
  features?: string[]
  min_users?: number | null
  max_users?: number | null
  billing_role?: PricingBillingRole
  /** Billing plans: users the plan fee covers, and the per-user prices. */
  included_users?: number
  admin_price?: string | null
  worker_price?: string | null
  base_price?: string | null
  minimum_price?: string | null
}

/**
 * A package's price in one currency: a number, or text such as "Let's talk" when amount is empty.
 * On a billing plan, amount is the Admin / Manager price per user a month, beside the Field Worker
 * price, the plan fee (covering the package's included_users) and the plan's minimum.
 */
export interface PricingPrice {
  amount: string | null
  price_label: string
  worker_amount?: string | null
  base_amount?: string | null
  minimum_amount?: string | null
}

export interface PricingCurrencyRow {
  code: string
  symbol: string
  tax_note: string
  /** ISO alpha-2 codes whose visitors see this currency first. */
  countries: string[]
  is_default: boolean
}

export type PricingStyle = 'free' | 'featured' | 'standard' | 'enterprise'
/** What billing charges from a card: '' = display only. */
export type PricingBillingRole = '' | 'tier'

export interface PricingPackageRow {
  key: string
  user_range_label: string
  min_users: number | null
  max_users: number | null
  /** Billing plans: users the plan fee covers before per-user prices apply. */
  included_users: number
  title: string
  description: string
  features: string[]
  price_suffix: string
  cta_label: string
  cta_href: string
  style: PricingStyle
  billing_role: PricingBillingRole
  is_active: boolean
  prices: Record<string, PricingPrice>
}

/** Everything the Pricing Plans editor changes (Org/MasterAdmin/Pricing/). */
export interface PricingDocument {
  currencies: PricingCurrencyRow[]
  packages: PricingPackageRow[]
  styles: { value: PricingStyle; label: string }[]
  billing_roles: { value: PricingBillingRole; label: string }[]
  annual_discount_percent: number
}

export interface FacilityProPricing {
  default_currency: string
  currency_by_country: Record<string, string>
  currencies: Record<string, { symbol: string; tax_note: string; tiers: PricingTier[] }>
}

export type HealthCheckStatus = 'ok' | 'warn' | 'fail' | 'skipped'
export type SystemHealthStatus = 'ok' | 'degraded' | 'down'

export interface HealthCheck {
  name: string
  status: HealthCheckStatus
  critical: boolean
  detail: string
  latency_ms: number | null
  data: Record<string, unknown>
}

export interface SystemHealthReport {
  status: SystemHealthStatus
  /** One line naming each failing check, e.g. "DOWN - Database: ..."; "ok" when healthy. Same text as the Slack alert. */
  summary?: string
  checked_at: string
  host: string
  version: string | null
  checks: HealthCheck[]
}

export const ORG_STATUS_CODE = { pending: 0, approved: 1, rejected: 2, suspended: 3, terminated: 4 } as const

export type OrgStatusAction = 'suspend' | 'reactivate' | 'terminate'

export type MasterAdminRole = 'owner' | 'operations' | 'support' | 'viewer'

export type MasterAdminPermission =
  | 'view' | 'support_reply' | 'org_status' | 'support_access' | 'org_terminate' | 'settings_edit' | 'staff_manage'

export interface MasterAdminMe {
  role: MasterAdminRole
  role_label: string
  permissions: MasterAdminPermission[]
}

export interface MasterAdminStaffMember {
  post_id: number
  user_id: number
  name: string
  email: string
  /** null: a root-org login with no master admin access. */
  role: MasterAdminRole | null
  role_label: string
  /** The root org's own administrator: always Owner, can't be changed or removed. */
  is_owner_account: boolean
  is_you: boolean
  /** Still on the emailed temporary password. */
  invite_pending: boolean
  active: boolean
  added_on: string | null
  invited_by: string
}

export interface MasterAdminRoleOption {
  value: MasterAdminRole
  label: string
  description: string
  permissions: MasterAdminPermission[]
}

export interface MasterAdminTeam {
  members: MasterAdminStaffMember[]
  roles: MasterAdminRoleOption[]
  invite_password_days: number
}

export interface StaffInviteResult {
  member: MasterAdminStaffMember
  email_sent: boolean
  /** Only when the email couldn't be sent, so the Owner can pass it on. */
  credentials?: { business_code: string; username: string; password: string; expires_at: string }
}

export type StaffAuditAction = 'staff_invite' | 'staff_role_change' | 'staff_remove' | 'staff_resend_invite'

export interface MasterAdminAuditEntry {
  id: number
  action: OrgStatusAction | 'support_access' | 'settings_update' | 'pricing_update' | StaffAuditAction
  action_label: string
  actor_name: string
  organization_id: number | null
  organization_name: string
  reason: string
  previous_status: string | null
  new_status: string | null
  details: Record<string, unknown>
  ip_address: string | null
  created_at: string
}

export interface SupportRole {
  id: string
  title: string
  hidden: boolean
}

export interface SupportAccessCredentials {
  tenant: string
  username: string
  password: string
}

const ORG_ACTION_PATH: Record<OrgStatusAction, 'Suspend' | 'Reactivate' | 'Terminate'> = {
  suspend: 'Suspend',
  reactivate: 'Reactivate',
  terminate: 'Terminate',
}

export const masterAdminQueryKeys = {
  overview: ['master-admin', 'overview'] as const,
  support: ['master-admin', 'support'] as const,
  invoices: ['master-admin', 'invoices'] as const,
  billing: ['master-admin', 'billing'] as const,
  integrationCosts: (month: string) => ['master-admin', 'integration-costs', month] as const,
  pricing: ['master-admin', 'pricing'] as const,
  pricingDocument: ['master-admin', 'pricing-document'] as const,
  systemHealth: ['master-admin', 'system-health'] as const,
  auditLog: (organizationId?: number) => ['master-admin', 'audit-log', organizationId ?? 'all'] as const,
  supportRoles: (organizationId: number) => ['master-admin', 'support-roles', organizationId] as const,
  me: ['master-admin', 'me'] as const,
  team: ['master-admin', 'team'] as const,
}

export interface MasterAdminSupportList {
  requests: HelpSupportRequest[]
  /** Team members who can answer support (assignable). */
  assignees: HelpSupportAssignee[]
  /** The signed-in user's id, for "Assigned to me". */
  me: number | null
}

export const masterAdminApi = {
  overview: async (): Promise<MasterAdminOverview> => {
    const response = await api.get<ApiResponse<MasterAdminOverview>>(API_PATHS.masterAdmin.overview)
    return response.data.data
  },
  supportList: async (): Promise<MasterAdminSupportList> => {
    const response = await api.get<ApiResponse<MasterAdminSupportList>>(API_PATHS.masterAdmin.supportList)
    return response.data.data
  },
  /** A reply is emailed to the customer; an internal note stays with the team. */
  supportMessage: async (id: number, kind: 'reply' | 'note', body: string, files: File[] = []): Promise<HelpSupportRequest> => {
    const { data, config } = helpMessageBody({ body }, files)
    const path = kind === 'note' ? API_PATHS.masterAdmin.supportNote(id) : API_PATHS.masterAdmin.supportReply(id)
    const response = await api.post<ApiResponse<HelpSupportRequest>>(path, data, config)
    return response.data.data
  },
  supportAssign: async (id: number, userId: number | null): Promise<HelpSupportRequest> => {
    const response = await api.post<ApiResponse<HelpSupportRequest>>(API_PATHS.masterAdmin.supportAssign(id), { user_id: userId ?? '' })
    return response.data.data
  },
  supportUpdate: async (id: number, status: HelpSupportStatus): Promise<HelpSupportRequest> => {
    const response = await api.post<ApiResponse<HelpSupportRequest>>(API_PATHS.masterAdmin.supportUpdate(id), { status })
    return response.data.data
  },
  invoices: async (): Promise<{ configured: boolean; invoices: MasterAdminInvoice[] }> => {
    const response = await api.get<ApiResponse<{ configured: boolean; invoices: MasterAdminInvoice[] }>>(
      API_PATHS.masterAdmin.invoices,
    )
    return response.data.data
  },
  billing: async (): Promise<MasterAdminBillingReport> => {
    const response = await api.get<ApiResponse<MasterAdminBillingReport>>(API_PATHS.masterAdmin.billing)
    return response.data.data
  },
  integrationCosts: async (month: string): Promise<IntegrationCostReport> => {
    const response = await api.get<ApiResponse<IntegrationCostReport>>(API_PATHS.masterAdmin.integrationCosts, { params: { month } })
    return response.data.data
  },
  extendTrial: async (id: number, body: { days: number; reason: string }): Promise<{ organization: MasterAdminOrganization; stripe_updated: boolean }> => {
    const response = await api.post<ApiResponse<{ organization: MasterAdminOrganization; stripe_updated: boolean }>>(
      API_PATHS.masterAdmin.orgAction(id, 'ExtendTrial'),
      body,
    )
    return response.data.data
  },
  pricing: async (): Promise<FacilityProPricing> => {
    const response = await api.get<ApiResponse<FacilityProPricing>>(API_PATHS.masterAdmin.facilityProPricing)
    return response.data.data
  },
  systemHealth: async (): Promise<SystemHealthReport> => {
    const response = await api.get<ApiResponse<SystemHealthReport>>(API_PATHS.masterAdmin.systemHealth)
    return response.data.data
  },
  /** Suspending a parent org blocks every company under it; it needs the name typed. */
  changeParentOrgStatus: async (
    id: number,
    action: 'suspend' | 'reactivate',
    body: { reason: string; confirm_name?: string },
  ): Promise<{ id: number; status: string; status_code: number }> => {
    const response = await api.post<ApiResponse<{ parent_org: { id: number; status: string; status_code: number } }>>(
      API_PATHS.masterAdmin.parentOrgAction(id, action === 'suspend' ? 'Suspend' : 'Reactivate'),
      body,
    )
    return response.data.data.parent_org
  },
  changeOrgStatus: async (
    id: number,
    action: OrgStatusAction,
    body: { reason: string; confirm_name?: string },
  ): Promise<MasterAdminOrganization> => {
    const response = await api.post<ApiResponse<{ organization: MasterAdminOrganization }>>(
      API_PATHS.masterAdmin.orgAction(id, ORG_ACTION_PATH[action]),
      body,
    )
    return response.data.data.organization
  },
  supportRoles: async (id: number): Promise<SupportRole[]> => {
    const response = await api.get<ApiResponse<{ roles: SupportRole[] }>>(API_PATHS.masterAdmin.orgAction(id, 'SupportRoles'))
    return response.data.data.roles
  },
  supportAccess: async (id: number, reason: string, role: string): Promise<SupportAccessCredentials> => {
    const response = await api.post<ApiResponse<{ credentials: SupportAccessCredentials }>>(
      API_PATHS.masterAdmin.orgAction(id, 'SupportAccess'),
      { reason, role },
    )
    return response.data.data.credentials
  },
  updateSettings: async (body: PlatformSettingsUpdate): Promise<PlatformSettings> => {
    const response = await api.post<ApiResponse<PlatformSettings>>(API_PATHS.masterAdmin.settingsUpdate, body)
    return response.data.data
  },
  pricingDocument: async (): Promise<PricingDocument> => {
    const response = await api.get<ApiResponse<PricingDocument>>(API_PATHS.masterAdmin.pricingDocument)
    return response.data.data
  },
  updatePricing: async (
    body: Pick<PricingDocument, 'currencies' | 'packages'> & { reason: string },
  ): Promise<PricingDocument> => {
    const response = await api.post<ApiResponse<PricingDocument>>(API_PATHS.masterAdmin.pricingUpdate, body)
    return response.data.data
  },
  me: async (): Promise<MasterAdminMe> => {
    const response = await api.get<ApiResponse<MasterAdminMe>>(API_PATHS.masterAdmin.me)
    return response.data.data
  },
  team: async (): Promise<MasterAdminTeam> => {
    const response = await api.get<ApiResponse<MasterAdminTeam>>(API_PATHS.masterAdmin.staff)
    return response.data.data
  },
  inviteStaff: async (body: { first_name: string; last_name: string; email: string; role: MasterAdminRole }): Promise<StaffInviteResult> => {
    const response = await api.post<ApiResponse<StaffInviteResult>>(API_PATHS.masterAdmin.staffInvite, body)
    return response.data.data
  },
  changeStaffRole: async (postId: number, role: MasterAdminRole): Promise<MasterAdminStaffMember> => {
    const response = await api.post<ApiResponse<{ member: MasterAdminStaffMember }>>(API_PATHS.masterAdmin.staffAction(postId, 'Role'), { role })
    return response.data.data.member
  },
  removeStaff: async (postId: number): Promise<void> => {
    await api.post(API_PATHS.masterAdmin.staffAction(postId, 'Remove'), {})
  },
  resendStaffInvite: async (postId: number): Promise<StaffInviteResult> => {
    const response = await api.post<ApiResponse<StaffInviteResult>>(API_PATHS.masterAdmin.staffAction(postId, 'ResendInvite'), {})
    return response.data.data
  },
  auditLog: async (organizationId?: number): Promise<MasterAdminAuditEntry[]> => {
    const response = await api.get<ApiResponse<{ entries: MasterAdminAuditEntry[] }>>(API_PATHS.masterAdmin.auditLog, {
      params: organizationId ? { organization_id: organizationId } : undefined,
    })
    return response.data.data.entries
  },
}
